import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { defaultProductItem, ProductItemType } from "./ProductsPicker";
import NumericFormatFieldDebounce from "../../components/ui/NumericFormatFieldDebounce";
import ItemSelect from "../../components/ItemSelect";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import { formatAmount } from "../../api/DataTypeUtils";

type ProductsPickerItemProps = {
  idx: number;
  projectId: string;
  currency: string;
  productItem: ProductItemType;
  onChange: (productItem: ProductItemType) => void;
  onRemove: (idx: number) => void;
};

const ProductsPickerItem = ({
  idx,
  projectId,
  currency,
  productItem,
  onChange,
  onRemove,
}: ProductsPickerItemProps) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="w-full flex items-center justify-between space-x-2 pb-2">
      <div className="flex items-center gap-2">
        <div className="w-60">
          <ItemSelect
            projectId={projectId}
            contentType="PRODUCT"
            type="FORM"
            value={productItem.id ? { value: productItem.id, label: productItem.name } : undefined}
            onChange={(product: any) => {
              if (product) {
                onChange({
                  id: product.value,
                  name: product.label,
                  productCode: product.productCode,
                  unitPrice: product.unitPrice || 0,
                  quantity: 1,
                  tax: product.tax || 0,
                  imageIds: product.imageIds,
                });
              } else {
                onChange({
                  ...defaultProductItem,
                });
              }
            }}
            placeholder={t("Select product")}
            isClearable
          />
        </div>
        <div className="w-30">
          <NumericFormatFieldDebounce
            value={`${productItem.unitPrice || 0}`}
            onChange={(value: string) => {
              onChange({
                ...productItem,
                unitPrice: value ? parseFloat(value) : 0,
              });
            }}
            currency={currency}
            disabled={!productItem.id}
          />
        </div>
        <div className="w-20">
          <InputFieldDebounce
            type="number"
            value={`${productItem.quantity || 0}`}
            onChange={(value: string) => {
              onChange({
                ...productItem,
                quantity: value ? parseInt(value) : 1,
              });
            }}
            disabled={!productItem.id}
          />
        </div>
        <div className="w-20">
          <NumericFormatFieldDebounce
            value={`${productItem.tax || 0}`}
            onChange={(value: string) => {
              onChange({
                ...productItem,
                tax: value ? parseFloat(value) : 0,
              });
            }}
            disabled={!productItem.id}
            isPercentage
          />
        </div>
        <div className="w-30">
          <InputFieldDebounce
            value={formatAmount(
              i18n.language,
              productItem.unitPrice * productItem.quantity || 0,
              currency,
              "0"
            )}
            disabled
          />
        </div>
      </div>
      <div className="p-1 flex items-center rounded-md hover:bg-gray-100">
        <X
          className={"w-5 h-5 text-gray-400 cursor-pointer"}
          onClick={() => {
            onRemove(idx);
          }}
        />
      </div>
    </div>
  );
};

export default ProductsPickerItem;
